/**
 * Provider Configurations
 *
 * LƯU Ý:
 * - Chỉ có Viettel USB Token đã được VERIFIED (từ viettelCAProvider.ts code cũ)
 * - Các provider khác là PLACEHOLDER, chưa được implement/verify
 * - CA type hiện tại không được hỗ trợ (fallback về IMAGE)
 *
 * Khi thêm provider mới:
 * 1. Thêm config vào các object bên dưới
 * 2. Đánh dấu status: VERIFIED hoặc PLACEHOLDER
 */

import { SignProvider } from '../../types';
import { USBEndpointConfig } from './GenericUSBSigner';
import { HSMEndpointConfig } from './GenericHSMSigner';
import { CAEndpointConfig } from './GenericCASigner';

// ========================================
// USB Provider Configurations
// ========================================
export const USB_PROVIDER_CONFIGS: Record<string, USBEndpointConfig> = {
  /**
   * Viettel USB Token - VERIFIED
   * Từ viettelCAProvider.ts (code cũ):
   * - Port: 14007
   * - Domain: http://127.0.0.1:14007/
   */
  [SignProvider.VIETTEL]: {
    provider: SignProvider.VIETTEL,
    displayName: 'Viettel USB Token',
    port: 14007, // VERIFIED: từ viettelCAProvider.ts
    libList: 'viettel-ca_v6.dll;viettel-ca_v5.dll;viettel-ca_v4.dll;viettel-ca_v2.dll', // VERIFIED
    endpoints: {
      getSession: '/getSession',       // VERIFIED
      getCertificate: '/getCertificate', // VERIFIED
      signFile: '/fileSign',           // VERIFIED
      signXml: '/signXMLData',         // VERIFIED
      getLastError: '/getLastErr',     // VERIFIED
    },
    requestFormat: 'form',
    sessionParam: 'sessionID',
  },

  /**
   * VNPT USB Token - PLACEHOLDER
   * TODO: Cần xác nhận port và endpoints thực tế từ VNPT
   */
  [SignProvider.VNPT]: {
    provider: SignProvider.VNPT,
    displayName: 'VNPT USB Token',
    port: 14008, // PLACEHOLDER: chưa xác nhận
    endpoints: {
      getSession: '/api/init',         // PLACEHOLDER
      getCertificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
    },
    requestFormat: 'json',
    sessionParam: 'sessionId',
  },

  /**
   * FPT USB Token - PLACEHOLDER
   * TODO: Cần xác nhận port và endpoints thực tế từ FPT
   */
  [SignProvider.FPT]: {
    provider: SignProvider.FPT,
    displayName: 'FPT USB Token',
    port: 14009, // PLACEHOLDER: chưa xác nhận
    endpoints: {
      getSession: '/api/session',      // PLACEHOLDER
      getCertificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign',           // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
    },
    requestFormat: 'json',
  },

  /**
   * EasyCA USB Token - PLACEHOLDER
   * TODO: Cần xác nhận port và endpoints thực tế từ EasyCA
   */
  [SignProvider.EASY]: {
    provider: SignProvider.EASY,
    displayName: 'EasyCA USB Token',
    port: 14010, // PLACEHOLDER: chưa xác nhận
    endpoints: {
      getSession: '/api/session',      // PLACEHOLDER
      getCertificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign',           // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
    },
    requestFormat: 'json',
  },

  /**
   * BKAV USB Token - PLACEHOLDER
   * TODO: Cần xác nhận port và endpoints thực tế từ BKAV
   */
  [SignProvider.BKAV]: {
    provider: SignProvider.BKAV,
    displayName: 'BKAV USB Token',
    port: 14011, // PLACEHOLDER: chưa xác nhận
    endpoints: {
      getSession: '/api/init',         // PLACEHOLDER
      getCertificate: '/api/cert',     // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
    },
    requestFormat: 'json',
  },
};

// ========================================
// HSM Provider Configurations - ALL PLACEHOLDER
// TODO: HSM chưa được implement trong code cũ
// ========================================
export const HSM_PROVIDER_CONFIGS: Record<string, Omit<HSMEndpointConfig, 'serverUrl' | 'apiKey'>> = {
  [SignProvider.VIETTEL]: {
    provider: SignProvider.VIETTEL,
    displayName: 'Viettel HSM',
    endpoints: {
      auth: '/api/auth/token',        // PLACEHOLDER
      certificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',     // PLACEHOLDER
      signXml: '/api/sign/xml',       // PLACEHOLDER
      signBatch: '/api/sign/batch',   // PLACEHOLDER
      health: '/health',              // PLACEHOLDER
    },
    authHeader: 'Authorization',
    tokenPrefix: 'Bearer',
  },

  [SignProvider.VNPT]: {
    provider: SignProvider.VNPT,
    displayName: 'VNPT HSM',
    endpoints: {
      auth: '/api/v1/auth',            // PLACEHOLDER
      certificate: '/api/v1/certificate', // PLACEHOLDER
      signFile: '/api/v1/sign/file',   // PLACEHOLDER
      signXml: '/api/v1/sign/xml',     // PLACEHOLDER
      signBatch: '/api/v1/sign/batch', // PLACEHOLDER
    },
  },

  [SignProvider.ICORP]: {
    provider: SignProvider.ICORP,
    displayName: 'iCorp HSM',
    endpoints: {
      auth: '/api/auth',               // PLACEHOLDER
      certificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
      signBatch: '/api/sign/batch',    // PLACEHOLDER
    },
  },

  [SignProvider.FPT]: {
    provider: SignProvider.FPT,
    displayName: 'FPT HSM',
    endpoints: {
      auth: '/auth/token',             // PLACEHOLDER
      certificate: '/certificate',     // PLACEHOLDER
      signFile: '/sign/file',          // PLACEHOLDER
      signXml: '/sign/xml',            // PLACEHOLDER
      signBatch: '/sign/batch',        // PLACEHOLDER
    },
  },
};

// ========================================
// CA (Remote) Provider Configurations - ALL PLACEHOLDER
// LƯU Ý: CA type KHÔNG ĐƯỢC HỖ TRỢ trong code cũ
// Hiện tại sẽ fallback về IMAGE type
// ========================================
export const CA_PROVIDER_CONFIGS: Record<string, Omit<CAEndpointConfig, 'serverUrl' | 'clientId' | 'clientSecret' | 'redirectUrl'>> = {
  [SignProvider.VIETTEL]: {
    provider: SignProvider.VIETTEL,
    displayName: 'Viettel Remote CA',
    endpoints: {
      requestOtp: '/api/otp/request',  // PLACEHOLDER
      verifyOtp: '/api/otp/verify',    // PLACEHOLDER
      oauthAuthorize: '/oauth/authorize', // PLACEHOLDER
      oauthToken: '/oauth/token',      // PLACEHOLDER
      certificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
      signBatch: '/api/sign/batch',    // PLACEHOLDER
    },
  },

  [SignProvider.VNPT]: {
    provider: SignProvider.VNPT,
    displayName: 'VNPT Remote CA',
    endpoints: {
      requestOtp: '/api/otp/send',     // PLACEHOLDER
      verifyOtp: '/api/otp/verify',    // PLACEHOLDER
      oauthAuthorize: '/oauth/authorize', // PLACEHOLDER
      oauthToken: '/oauth/token',      // PLACEHOLDER
      certificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
      signBatch: '/api/sign/batch',    // PLACEHOLDER
    },
  },

  [SignProvider.MYSIGN]: {
    provider: SignProvider.MYSIGN,
    displayName: 'MySign Remote CA',
    endpoints: {
      requestOtp: '/otp/request',      // PLACEHOLDER
      verifyOtp: '/otp/verify',        // PLACEHOLDER
      oauthAuthorize: '/oauth/authorize', // PLACEHOLDER
      oauthToken: '/oauth/token',      // PLACEHOLDER
      certificate: '/certificate',     // PLACEHOLDER
      signFile: '/sign/file',          // PLACEHOLDER
      signXml: '/sign/xml',            // PLACEHOLDER
      signBatch: '/sign/batch',        // PLACEHOLDER
    },
  },

  [SignProvider.EASY]: {
    provider: SignProvider.EASY,
    displayName: 'EasyCA Remote',
    endpoints: {
      requestOtp: '/api/otp/request',  // PLACEHOLDER
      verifyOtp: '/api/otp/verify',    // PLACEHOLDER
      oauthAuthorize: '/oauth/authorize', // PLACEHOLDER
      oauthToken: '/oauth/token',      // PLACEHOLDER
      certificate: '/api/certificate', // PLACEHOLDER
      signFile: '/api/sign/file',      // PLACEHOLDER
      signXml: '/api/sign/xml',        // PLACEHOLDER
      signBatch: '/api/sign/batch',    // PLACEHOLDER
    },
  },
};

// ========================================
// Default Server URLs - ALL PLACEHOLDER (trừ localhost)
// LƯU Ý: Các URL này chưa được xác nhận
// Code cũ chỉ sử dụng localhost cho USB Token
// ========================================
export const DEFAULT_SERVER_URLS: Record<SignProvider, string> = {
  [SignProvider.VIETTEL]: '',    // USB Token dùng localhost:14007
  [SignProvider.VNPT]: '',       // PLACEHOLDER
  [SignProvider.FPT]: '',        // PLACEHOLDER
  [SignProvider.MYSIGN]: '',     // PLACEHOLDER
  [SignProvider.ICORP]: '',      // PLACEHOLDER
  [SignProvider.EASY]: '',       // PLACEHOLDER
  [SignProvider.BKAV]: '',       // PLACEHOLDER
  [SignProvider.NACENCOMM]: '',  // PLACEHOLDER
  [SignProvider.OCEANTECH]: '',  // IMAGE type - không cần URL
};
